import { createContext, use, useState, ReactNode, useCallback } from "react";
import type { ProgressBarConfig } from "constants/progressBarConfig";

type ProgressBarContextType = {
    progressConfig: ProgressBarConfig | null;
    setProgressConfig: (config: ProgressBarConfig | null) => void;
    updateStepCompletion: (stepIndex: number, completion: number) => void;
};

const ProgressBarContext = createContext<ProgressBarContextType | undefined>(undefined);

export const ProgressBarProvider = ({ children }: { children: ReactNode }) => {
    const [progressConfig, setProgressConfig] = useState<ProgressBarConfig | null>(null);

    const updateStepCompletion = useCallback((stepIndex: number, completion: number) => {
        setProgressConfig((prev) => {
            if (!prev) return prev;

            const newStepCompletion = [...prev.stepCompletion];
            newStepCompletion[stepIndex] = Math.min(100, Math.max(0, completion));

            return {
                ...prev,
                stepCompletion: newStepCompletion,
            };
        });
    }, []);

    return (
        <ProgressBarContext.Provider value={{ progressConfig, setProgressConfig, updateStepCompletion }}>
            {children}
        </ProgressBarContext.Provider>
    );
};

export const useProgressBar = () => {
    const context = use(ProgressBarContext);
    if (!context) {
        throw new Error("useProgressBar must be used within a ProgressBarProvider");
    }
    return context;
};
